import React, { useMemo, useState } from "react";

function FilterMenu({ colIndex, rows, currentFilter, onApply, onClose }) {
  const [searchTerm, setSearchTerm] = useState("");

  const allValues = useMemo(() => {
    const vals = new Set();
    // Skip row 0 (header row) — it should not appear as a filterable value
    rows.forEach((row, idx) => {
      if (idx === 0) return;
      const v = row[colIndex];
      vals.add(v == null || v === "" ? "(Blank)" : String(v));
    });
    const arr = Array.from(vals);
    // Sort numerically for number values, then alphabetically for text
    arr.sort((a, b) => {
      const aNum = Number(a);
      const bNum = Number(b);
      const aIsNum = a !== "(Blank)" && a !== "" && !isNaN(aNum);
      const bIsNum = b !== "(Blank)" && b !== "" && !isNaN(bNum);
      if (aIsNum && bIsNum) return aNum - bNum;
      if (aIsNum) return -1;
      if (bIsNum) return 1;
      return a.localeCompare(b);
    });
    return arr;
  }, [colIndex, rows]);

  const [selected, setSelected] = useState(() => {
    if (currentFilter) return new Set(currentFilter);
    return new Set(allValues);
  });

  const filteredValues = useMemo(() => {
    if (!searchTerm) return allValues;
    const lower = searchTerm.toLowerCase();
    return allValues.filter(v => v.toLowerCase().includes(lower));
  }, [allValues, searchTerm]);

  const isAllSelected = selected.size === allValues.length;
  const isAllFilteredSelected = filteredValues.every(v => selected.has(v));

  const toggleAll = () => {
    if (isAllFilteredSelected) {
      const next = new Set(selected);
      filteredValues.forEach(v => next.delete(v));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredValues.forEach(v => next.add(v));
      setSelected(next);
    }
  };

  const toggleValue = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setSelected(next);
  };

  return (
    <div className="filter-popup" onClick={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}>
      <div className="filter-search-container">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="filter-search-input"
          autoFocus
        />
      </div>
      <div className="filter-popup-list">
        {filteredValues.length > 0 && (
          <label className="filter-popup-item select-all-item">
            <input
              type="checkbox"
              checked={isAllFilteredSelected}
              onChange={toggleAll}
            />
            <span>(Select All Search Results)</span>
          </label>
        )}
        {filteredValues.map(val => (
          <label key={val} className="filter-popup-item">
            <input type="checkbox" checked={selected.has(val)} onChange={() => toggleValue(val)} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
          </label>
        ))}
        {filteredValues.length === 0 && (
          <div style={{ padding: '8px', color: '#666', fontSize: '12px' }}>No matches found</div>
        )}
      </div>
      <div className="filter-popup-footer">
        <button
          type="button"
          className="filter-btn-ok"
          disabled={selected.size === 0}
          aria-disabled={selected.size === 0}
          onClick={() => {
            onApply(selected.size === allValues.length ? null : selected);
            onClose();
          }}
        >
          OK
        </button>
        <button type="button" className="filter-btn-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default FilterMenu;
